import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | null;
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  return <>
      <footer className="py-12 text-center text-sm bg-olive-900/5" data-id="element-14">
        <p className="text-stone-400 mb-4" data-id="element-15">
          &copy; {new Date().getFullYear()} Blue Zone Longevity. All rights
          reserved.
        </p>
        <div className="flex items-center justify-center gap-6" data-id="element-16">
          <button onClick={() => setActiveModal('privacy')} className="text-stone-400 hover:text-olive-700 transition-colors underline underline-offset-2" data-id="element-17">
            Privacy Policy
          </button>
          <span className="text-stone-300" data-id="element-18">·</span>
          <button onClick={() => setActiveModal('terms')} className="text-stone-400 hover:text-olive-700 transition-colors underline underline-offset-2" data-id="element-19">
            Terms of Service
          </button>
        </div>
      </footer>

      <AnimatePresence data-id="element-20">
        {activeModal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setActiveModal(null)} data-id="element-21">
            {/* Backdrop */}
            <div className="absolute inset-0 bg-olive-900/60 backdrop-blur-sm" data-id="element-22" />

            {/* Modal */}
            <motion.div initial={{
          opacity: 0,
          y: 20,
          scale: 0.97
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: 10,
          scale: 0.97
        }} transition={{
          duration: 0.25,
          ease: 'easeOut'
        }} onClick={e => e.stopPropagation()} className="relative bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden z-10" data-id="element-23">
              {/* Header */}
              <div className="flex items-center justify-between px-8 py-5 border-b border-olive-100 sticky top-0 bg-white z-10" data-id="element-24">
                <h2 className="text-xl font-serif text-olive-900" data-id="element-25">
                  {activeModal === 'privacy' ? 'Privacy Policy' : 'Terms of Service'}
                </h2>
                <button onClick={() => setActiveModal(null)} className="text-stone-400 hover:text-olive-700 transition-colors p-1" data-id="element-26">
                  <X className="w-5 h-5" data-id="element-27" />
                </button>
              </div>

              {/* Content */}
              <div className="px-8 py-6 overflow-y-auto max-h-[calc(80vh-72px)] text-stone-600 leading-relaxed space-y-5 text-sm" data-id="element-28">
                {activeModal === 'privacy' ? <PrivacyContent data-id="element-29" /> : <TermsContent data-id="element-30" />}
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}
function PrivacyContent() {
  return <>
      <p className="text-xs text-stone-400 uppercase tracking-widest" data-id="element-31">
        Last updated: February 2026
      </p>

      <h3 className="font-serif text-lg text-olive-900 pt-2" data-id="element-32">
        Information We Collect
      </h3>
      <p data-id="element-33">
        When you purchase the Blue Zone Longevity Mindset Guide, we collect only
        the information necessary to process your order, including your name,
        email address, and payment details. Payment processing is handled
        securely by our third-party payment provider.
      </p>

      <h3 className="font-serif text-lg text-olive-900 pt-2" data-id="element-34">
        How We Use Your Information
      </h3>
      <p data-id="element-35">
        We use your information solely to deliver the digital product you
        purchased and to send you important updates related to your purchase. We
        do not sell, rent, or share your personal information with third parties
        for marketing purposes.
      </p>

      <h3 className="font-serif text-lg text-olive-900 pt-2" data-id="element-36">
        Cookies &amp; Tracking
      </h3>
      <p data-id="element-37">
        Our website uses cookies and tracking pixels (including Meta Pixel) to
        understand how visitors interact with our page. This data helps us
        improve the user experience. No personally identifiable information is
        shared through these tools beyond what is standard for web analytics.
      </p>

      <h3 className="font-serif text-lg text-olive-900 pt-2" data-id="element-38">Data Security</h3>
      <p data-id="element-39">
        We implement industry-standard security measures to protect your
        personal data. All transactions are encrypted using SSL technology.
      </p>

      <h3 className="font-serif text-lg text-olive-900 pt-2" data-id="element-40">Your Rights</h3>
      <p data-id="element-41">
        You have the right to request access to, correction of, or deletion of
        your personal data at any time. To exercise these rights, please contact
        us through our website.
      </p>
    </>;
}
function TermsContent() {
  return <>
      <p className="text-xs text-stone-400 uppercase tracking-widest" data-id="element-42">
        Last updated: February 2026
      </p>

      <h3 className="font-serif text-lg text-olive-900 pt-2" data-id="element-43">
        Digital Product Delivery
      </h3>
      <p data-id="element-44">
        The Blue Zone Longevity Mindset Guide is a digital product delivered
        instantly upon purchase. You will receive access to download the guide
        immediately after your payment is confirmed.
      </p>

      <h3 className="font-serif text-lg text-olive-900 pt-2" data-id="element-45">
        Intellectual Property
      </h3>
      <p data-id="element-46">
        All content within the Blue Zone Longevity Mindset Guide, including
        text, graphics, and methodologies, is protected by copyright. You are
        granted a personal, non-transferable license to use the guide for your
        own personal development. Redistribution, resale, or sharing of the
        guide is strictly prohibited.
      </p>

      <h3 className="font-serif text-lg text-olive-900 pt-2" data-id="element-47">Acceptable Use</h3>
      <p data-id="element-48">
        By purchasing this product, you agree to use it for personal purposes
        only. You may not reproduce, distribute, or create derivative works from
        the content without prior written consent.
      </p>

      <h3 className="font-serif text-lg text-olive-900 pt-2" data-id="element-49">Disclaimer</h3>
      <p data-id="element-50">
        The information provided in this guide is for educational and
        informational purposes only. It is not intended as medical advice.
        Always consult with a qualified healthcare professional before making
        changes to your health routine. Results may vary from person to person.
      </p>

      <h3 className="font-serif text-lg text-olive-900 pt-2" data-id="element-51">Governing Law</h3>
      <p data-id="element-52">
        These terms shall be governed by and construed in accordance with
        applicable laws. Any disputes arising from the use of this product shall
        be resolved through appropriate legal channels.
      </p>
    </>;
}